import type { EvmAddress, ZcashNetwork } from './types'

/**
 * What the browser build knows about the outside world.
 *
 * Only `VITE_`-prefixed variables reach the client; the ones in `.env`
 * without that prefix belong to the spikes, which run under Node. Nothing
 * secret goes here: anything in this file is readable by anyone who opens
 * the page, so a key that must stay private cannot live in a static app.
 *
 * Every value is optional. Missing configuration makes a movement refuse
 * with a reason, which is the behaviour the capability report depends on —
 * never a default that quietly does something less private.
 */

const read = (name: string): string | undefined => {
  const value = (import.meta.env as Record<string, string | undefined>)[name]
  return value && value.length > 0 ? value : undefined
}

const address = (name: string): EvmAddress | undefined => {
  const value = read(name)
  return value && /^0x[0-9a-fA-F]{40}$/.test(value) ? (value as EvmAddress) : undefined
}

export interface AppConfig {
  /**
   * The Arbitrum RPCs, in the order they were given.
   *
   * An RPC is unavoidable: the session has to send a transfer on Arbitrum,
   * and that means asking some node. What matters is what the node learns.
   *
   * Every call goes through `rpcOver`, which uses the network adapter, and
   * the checker refuses to move anything while that adapter is not private.
   * So the provider sees a Tor or Nym exit, not the person — and the address
   * it is asked about is on a public chain anyway, where a block explorer
   * shows the same thing to anyone.
   *
   * What one provider can still do is notice that two session wallets were
   * asked about by the same customer, and so link two sessions that have
   * nothing else in common. That is what a list fixes: `pickRpc` gives each
   * session a different endpoint, so no provider sees more than its share.
   *
   * There is no default. A public endpoint baked into the build would hand
   * one company that graph for every user of the app, and would be used by
   * everyone who never thought about it.
   */
  readonly rpcUrls: readonly string[]
  /** Raises the 1Click fee from 0.25% to 0.20%. Public by nature. */
  readonly oneClickJwt: string | undefined
  readonly bundlerUrl: string | undefined
  readonly paymasterAddress: EvmAddress | undefined
  readonly delegateAddress: EvmAddress | undefined
  /**
   * Which Zcash network this deployment is on. `main` unless set.
   *
   * It defaults to mainnet because that is the product, and because a
   * deployment that silently believed it was on testnet would accept
   * testnet addresses for real money. An unrecognised value is not
   * interpreted — it is reported as a gap, since guessing which network was
   * meant is the one thing worse than refusing.
   */
  readonly zcashNetwork: ZcashNetwork
  /**
   * Whether this bundle was built as a demo.
   *
   * It shortens the exit delay from ten-to-ninety minutes to a handful of
   * seconds, and it is half of what `unsafeLiveTest` needs. Both are things
   * nobody should be able to switch on in the build people are given, which
   * is why it is read from `import.meta.env` directly: Vite folds that, so
   * an ordinary build carries the constant `false` and no environment
   * variable can reach it afterwards.
   */
  readonly demoMode: boolean
  /**
   * Run the real adapters against the real venues with the privacy gate off.
   *
   * There is exactly one reason this exists: the end-to-end path cannot be
   * exercised locally otherwise. The checker refuses every movement while
   * the connection is not private, which is correct for the product and
   * useless for finding out whether 1Click, Lighter and the signer actually
   * work together. So this turns the gate into a warning — and nothing else.
   * Every adapter stays real, every signature is real, and every transfer
   * spends real money.
   *
   * What it costs, said plainly rather than buried: the venues see the
   * machine's own IP address, the two swap legs and the Lighter account are
   * linkable to it and to each other, and that linkage is permanent and
   * public. Use a throwaway amount and assume this session is deanonymised.
   *
   * It takes two build-time flags, not one: `VITE_UNSAFE_LIVE_TEST=1` on a
   * dev server, or that plus `VITE_DEMO_MODE=1` in a build. Both are folded
   * by Vite, so an ordinary `pnpm build` evaluates this to `false` and the
   * override cannot be reached however the environment is set afterwards.
   *
   * The second flag exists because a demo sometimes has to run where the
   * mixnet will not start — a venue network, a stage, a recording. That is
   * a real need and pretending otherwise just gets the gate commented out
   * under pressure. What it must not become is a switch sitting in the
   * build people are pointed at: making a demo bundle is then a deliberate
   * act with its own command, and the screen says what it costs while it is
   * on.
   */
  readonly unsafeLiveTest: boolean
}

/**
 * Split a comma-separated list, dropping blanks and duplicates.
 *
 * Duplicates are dropped because a list with the same endpoint twice would
 * quietly halve the rotation while looking like it had not.
 */
function list(name: string): readonly string[] {
  const raw = read(name)
  if (!raw) return []
  return [...new Set(raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0))]
}

/**
 * Which endpoint a session uses, by its key index.
 *
 * Deterministic rather than random, and that is the point. A random pick
 * would land two sessions on the same provider now and then — exactly the
 * event this is meant to prevent — and would re-roll every time the adapters
 * were rebuilt, scattering one session across several providers. Key indices
 * are consecutive, so any run of n sessions gets n different endpoints.
 */
export function pickRpc(urls: readonly string[], keyIndex: number): string | undefined {
  if (urls.length === 0) return undefined
  // Negative or fractional indices would wrap backwards or miss; neither
  // should ever happen, and neither should silently pick nothing.
  const i = Math.abs(Math.trunc(keyIndex)) % urls.length
  return urls[i]
}

export function readConfig(): AppConfig {
  return {
    rpcUrls: list('VITE_ARBITRUM_RPC_URLS').length
      ? list('VITE_ARBITRUM_RPC_URLS')
      : // The singular name still works, and is simply a list of one.
        list('VITE_ARBITRUM_RPC_URL'),
    oneClickJwt: read('VITE_ONECLICK_JWT'),
    bundlerUrl: read('VITE_BUNDLER_URL'),
    paymasterAddress: address('VITE_PAYMASTER_ADDRESS'),
    delegateAddress: address('VITE_DELEGATE_ADDRESS'),
    zcashNetwork: read('VITE_ZCASH_NETWORK') === 'test' ? 'test' : 'main',
    // Folded by Vite, so this is a constant in a build, not a lookup.
    demoMode: import.meta.env.VITE_DEMO_MODE === '1',
    // Two flags, both folded at build time, and both required. Read it as:
    // the override is consulted on a dev server, or in a bundle somebody
    // deliberately built as a demo — never in an ordinary build.
    unsafeLiveTest:
      (Boolean(import.meta.env.DEV) || import.meta.env.VITE_DEMO_MODE === '1') &&
      read('VITE_UNSAFE_LIVE_TEST') === '1',
  }
}

/** What is missing before the app can run against the real thing at all. */
export function configGaps(config: AppConfig): readonly string[] {
  const gaps: string[] = []
  if (config.rpcUrls.length === 0) {
    gaps.push('VITE_ARBITRUM_RPC_URLS, so nothing can read Arbitrum')
  }
  if (!config.bundlerUrl) gaps.push('VITE_BUNDLER_URL, so gas cannot be paid in USDC')
  if (!config.paymasterAddress) gaps.push('VITE_PAYMASTER_ADDRESS')
  if (!config.delegateAddress) gaps.push('VITE_DELEGATE_ADDRESS')
  // Set, but to neither of the two words it can be. Saying so beats running
  // on mainnet because someone wrote `testnet` and was not told.
  const network = read('VITE_ZCASH_NETWORK')
  if (network !== undefined && network !== 'main' && network !== 'test') {
    gaps.push(`VITE_ZCASH_NETWORK is "${network}", which is neither "main" nor "test"`)
  }
  return gaps
}
